'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getStoredLicense,
  storeLicense,
  clearLicense,
  validateLicenseKey,
  hasModuleAccess as checkModuleAccess,
  checkUploadAllowed,
  recordUpload,
  getUsage,
} from '@/lib/licenseKey';
import type { LicenseData, UsageData } from '@/lib/licenseKey';

interface LicenseContextType {
  license: LicenseData | null;
  loading: boolean;
  activateLicense: (key: string) => { success: boolean; error?: string };
  deactivateLicense: () => void;
  hasModuleAccess: (moduleName: string) => boolean;
  checkUploadAllowed: () => { allowed: boolean; reason?: string };
  recordUpload: () => void;
  usage: UsageData;
  refreshUsage: () => void;
}

const LicenseContext = createContext<LicenseContextType>({} as LicenseContextType);

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) throw new Error('useLicense must be used within LicenseProvider');
  return context;
};

export const LicenseProvider = ({ children }: { children: React.ReactNode }) => {
  const [license, setLicense] = useState<LicenseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState<UsageData>({ totalUploads: 0, dailyUploads: 0, lastUploadDate: '' });

  useEffect(() => {
    const stored = getStoredLicense();
    setLicense(stored);
    setUsage(getUsage());
    setLoading(false);
  }, []);

  const activateLicense = useCallback((key: string): { success: boolean; error?: string } => {
    const result = validateLicenseKey(key);
    if (!result.valid || !result.data) {
      return { success: false, error: result.error };
    }
    storeLicense(key);
    setLicense(result.data);
    setUsage(getUsage());
    return { success: true };
  }, []);

  const deactivateLicense = useCallback(() => {
    clearLicense();
    setLicense(null);
    setUsage({ totalUploads: 0, dailyUploads: 0, lastUploadDate: '' });
  }, []);

  const hasModuleAccessFn = useCallback(
    (moduleName: string) => checkModuleAccess(license, moduleName),
    [license]
  );

  const checkUploadAllowedFn = useCallback(() => {
    if (!license) return { allowed: false, reason: 'No active license.' };
    return checkUploadAllowed(license);
  }, [license]);

  const recordUploadFn = useCallback(() => {
    recordUpload();
    setUsage(getUsage());
  }, []);

  const refreshUsage = useCallback(() => {
    setUsage(getUsage());
  }, []);

  return (
    <LicenseContext.Provider
      value={{
        license,
        loading,
        activateLicense,
        deactivateLicense,
        hasModuleAccess: hasModuleAccessFn,
        checkUploadAllowed: checkUploadAllowedFn,
        recordUpload: recordUploadFn,
        usage,
        refreshUsage,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
};

export default LicenseContext;
